import {
  Box,
  Button,
  CloseButton,
  HStack,
  Icon,
  SlideFade,
  Spacer,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { TimeAndDate } from '@zap-onboard/api-client';
import { useEffect, useState } from 'react';
import { useCurrentBusinessId } from '../auth/selectors';
import { useCurrentBusiness } from '../hooks';
import { SuperAPISetupStore } from './stores/SuperAPISetupStore';
import {
  FaRegCircle,
  FaRegCheckCircle,
  FaBuilding,
  FaRegBuilding,
} from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { RiBankLine } from 'react-icons/ri';

let superAPIState = SuperAPISetupStore.get() ?? {};

SuperAPISetupStore.$remoteChanges.subscribe((newState) => {
  superAPIState = newState ?? superAPIState;
});

function markHiddenForBusiness(businessId: string) {
  const state = {
    ...superAPIState,
    [businessId]: {
      ...(superAPIState[businessId] ?? {}),
      hiddenAt: TimeAndDate.present(),
    },
  };
  superAPIState = state;
  SuperAPISetupStore.set(state);
}

function getIsHiddenForBusiness(
  businessId: string,
  businessCreatedAt: TimeAndDate | undefined,
) {
  const hiddenAt = superAPIState[businessId]?.hiddenAt;

  // Business must be at least 30 days old
  const canBeDisplayed =
    businessCreatedAt && businessCreatedAt.add({ days: 30 }).isInPast();

  if (!canBeDisplayed) {
    return true;
  }

  if (!hiddenAt) {
    return false;
  }

  return hiddenAt.add({ hours: 24 }).isInFuture();
}

export const useSuperAPISetupState = () => {
  const currentBusinessId = useCurrentBusinessId();
  const { data } = useCurrentBusiness();

  const loaded = !!data?.business;

  const needsABN = !data?.business.abn;
  const needsSuperNomination = !data?.config.nominatedSuperFund;
  const needsAnything = needsABN || needsSuperNomination;

  const businessCreatedAt = data?.config.createdAt;

  const [isExplicitlyHidden, setIsExplicitlyHidden] = useState<boolean>(false);
  // Don't copy this. I couldn't think of a more obtuse way to handle this
  // state
  useEffect(() => {
    const calculateState = (
      state: ReturnType<(typeof SuperAPISetupStore)['get']>,
    ) => {
      superAPIState = state ?? superAPIState;
      const hidden = getIsHiddenForBusiness(
        currentBusinessId ?? '',
        businessCreatedAt,
      );
      setIsExplicitlyHidden(hidden);
    };

    calculateState(SuperAPISetupStore.get());

    return SuperAPISetupStore.$remoteChanges.subscribe(calculateState);
  }, [currentBusinessId, isExplicitlyHidden, businessCreatedAt]);

  return {
    currentBusinessId,
    shouldDisplay:
      needsAnything && !isExplicitlyHidden && loaded && !!currentBusinessId,
    setIsExplicitlyHidden,
    needsABN,
    needsSuperNomination,
  };
};

const SUPER_API_TOAST_ID = 'SUPER_API_SETUP_NAGBOX';

export const SuperAPISetupNagbox = () => {
  const {
    needsABN,
    needsSuperNomination,
    shouldDisplay,
    currentBusinessId,
    setIsExplicitlyHidden,
  } = useSuperAPISetupState();

  const toast = useToast();

  const history = useHistory();

  const goToSettings = () => history.push('/business/settings');

  const sumOfTasks = [needsABN, needsSuperNomination].filter((v) => !!v).length;

  if (!currentBusinessId) {
    return null;
  }

  return (
    <Box
      position="fixed"
      zIndex="toast"
      bottom={4}
      left={4}
      maxW="sm"
      w="calc(100vw - 2rem)"
    >
      <SlideFade unmountOnExit in={shouldDisplay} offsetY="12px">
        <Box
          borderRadius="md"
          boxShadow="lg"
          layerStyle="base"
          bg="white"
          p={4}
        >
          <HStack>
            <Text fontWeight={700} fontSize="md">
              Get Ready for Payday Super
            </Text>
            <Spacer />
            <CloseButton
              onClick={() => {
                markHiddenForBusiness(currentBusinessId);
                setIsExplicitlyHidden(true);
              }}
            />
          </HStack>
          <Text mt={2} fontSize="sm" color="rgb(107, 114, 128)">
            Upcoming Payday Super changes mean super contributions will need to
            be processed alongside payroll. To help you prepare, please complete
            the following setup items.
          </Text>

          <Box
            p={2}
            role="group"
            onClick={goToSettings}
            cursor="pointer"
            borderRadius="md"
            _hover={{ bg: 'gray.50' }}
          >
            <HStack>
              <Icon
                as={needsABN ? FaRegCircle : FaRegCheckCircle}
                w={5}
                h={5}
                opacity={0.8}
                _groupHover={{ opacity: 1 }}
                color={needsABN ? 'rgb(40, 44, 52)' : '#06A77D'}
                size={24}
              />
              <HStack>
                <Icon as={FaRegBuilding} w={3.5} h={3.5} />
                <Text fontSize="sm">Add or confirm your business ABN</Text>
              </HStack>
            </HStack>
          </Box>

          <Box
            p={2}
            role="group"
            onClick={goToSettings}
            cursor="pointer"
            borderRadius="md"
            _hover={{ bg: 'gray.50' }}
          >
            <HStack>
              <Icon
                as={needsSuperNomination ? FaRegCircle : FaRegCheckCircle}
                w={5}
                h={5}
                opacity={0.8}
                _groupHover={{ opacity: 1 }}
                color={needsSuperNomination ? 'rgb(40, 44, 52)' : '#06A77D'}
                size={24}
              />
              <HStack>
                <Icon as={RiBankLine} w={3.5} h={3.5} />
                <Text fontSize="sm">
                  Select your employer default super fund
                </Text>
              </HStack>
            </HStack>
          </Box>

          <Text opacity={0.8} fontSize="xs">
            {sumOfTasks} out of 2 completed
          </Text>

          <Button mt={4} w="100%" borderRadius={8} onClick={goToSettings}>
            Review Super Settings
          </Button>
        </Box>
      </SlideFade>
    </Box>
  );
};
